import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

const EARTH_R = 2.0;
const EARTH_TEXTURE_URL = 'https://cdn.jsdelivr.net/gh/mrdoob/three.js@r128/examples/textures/planets/earth_atmos_2048.jpg';
const TRAIL_LEN = 60;

// Lagrange point positions (scaled for visualization, not to scale with actual distances)
const LAGRANGE_POINTS = {
  L1: { position: [-8, 0, 0], color: '#FCD34D', label: 'L1', description: 'Between Sun & Earth' },
  L2: { position: [8, 0, 0], color: '#F87171', label: 'L2', description: 'Behind Earth (JWST)' },
  L3: { position: [-25, 0, 0], color: '#A78BFA', label: 'L3', description: 'Opposite side of Sun' },
  L4: { position: [-12, 0, 12], color: '#34D399', label: 'L4', description: 'Leading Trojan point' },
  L5: { position: [-12, 0, -12], color: '#60A5FA', label: 'L5', description: 'Trailing Trojan point' },
};

export function computeOrbitPoints(semiMajor, eccentricity, inclination, raan, numPoints = 300) {
  const pts = [];
  const ecc = Math.min(Math.max(eccentricity || 0, 0), 0.99);
  const sm = semiMajor || 3;
  const b = sm * Math.sqrt(Math.max(0, 1 - ecc * ecc));
  const c = sm * ecc;
  const incRad = ((inclination || 0) * Math.PI) / 180;
  const raanRad = ((raan || 0) * Math.PI) / 180;
  for (let i = 0; i <= numPoints; i++) {
    const theta = (i / numPoints) * Math.PI * 2;
    const x = sm * Math.cos(theta) - c;
    const z = b * Math.sin(theta);
    const y_i = -z * Math.sin(incRad);
    const z_i = z * Math.cos(incRad);
    const x_r = x * Math.cos(raanRad) + z_i * Math.sin(raanRad);
    const z_r = -x * Math.sin(raanRad) + z_i * Math.cos(raanRad);
    pts.push(new THREE.Vector3(x_r, y_i, z_r));
  }
  return pts;
}

// Build Keplerian time array so satellites move faster near perigee
function buildTimeArray(points) {
  const weights = points.map(p => {
    const r = p.length();
    return r > 0 ? r * r : 0.001; // time ∝ r² (Kepler 2nd law)
  });
  const total = weights.reduce((a, b) => a + b, 0);
  const times = [0];
  for (let i = 1; i < weights.length; i++) {
    times.push(times[i - 1] + weights[i] / total);
  }
  return times;
}

// Binary search for time array lookup
function findIdx(times, t) {
  let lo = 0, hi = times.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] < t) lo = mid + 1; else hi = mid;
  }
  return lo;
}

// Camera control — one-shot transitions only, then free user control
function CameraControls({ enabled, targetRef }) {
  const { camera, gl } = useThree();
  const controlsRef = useRef(null);
  const lastTarget = useRef(new THREE.Vector3());
  const transitioning = useRef(false);
  const transProgress = useRef(0);
  const transFrom = useRef(new THREE.Vector3());

  useEffect(() => {
    if (!enabled) return;
    const controls = new OrbitControls(camera, gl.domElement);
    controls.enablePan = false;
    controls.minDistance = 3;
    controls.maxDistance = 80;
    controls.enableDamping = true;
    controls.dampingFactor = 0.07;
    controls.rotateSpeed = 0.8;
    controlsRef.current = controls;
    return () => controls.dispose();
  }, [camera, gl, enabled]);

  useFrame((_, delta) => {
    if (controlsRef.current) controlsRef.current.update();
    // Detect target change → one-shot animated transition, then stop
    if (targetRef?.current && !targetRef.current.equals(lastTarget.current)) {
      transFrom.current.copy(camera.position);
      lastTarget.current.copy(targetRef.current);
      transitioning.current = true;
      transProgress.current = 0;
    }
    if (transitioning.current) {
      transProgress.current = Math.min(transProgress.current + delta * 1.5, 1);
      const ease = 1 - Math.pow(1 - transProgress.current, 3);
      camera.position.lerpVectors(transFrom.current, lastTarget.current, ease);
      if (transProgress.current >= 1) transitioning.current = false;
    }
  });
  return null;
}

// Create a label sprite using canvas texture
function createLabelSprite(text, color) {
  const canvas = document.createElement('canvas');
  canvas.width = 256; canvas.height = 48;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, 256, 48);
  ctx.fillStyle = 'rgba(0,0,0,0.65)';
  ctx.roundRect?.(2, 2, 252, 44, 8) || ctx.fillRect(2, 2, 252, 44);
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.roundRect?.(2, 2, 252, 44, 8) || ctx.strokeRect(2, 2, 252, 44);
  ctx.stroke();
  ctx.font = 'bold 20px Rajdhani, sans-serif';
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 128, 24);
  const texture = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(2.2, 0.42, 1);
  return sprite;
}

// Create a Lagrange point marker with pulsing glow
function createLagrangeMarker(color) {
  const group = new THREE.Group();
  
  // Core sphere
  const coreGeo = new THREE.SphereGeometry(0.15, 16, 16);
  const coreMat = new THREE.MeshBasicMaterial({ color });
  const core = new THREE.Mesh(coreGeo, coreMat);
  group.add(core);
  
  // Outer ring
  const ringGeo = new THREE.RingGeometry(0.3, 0.35, 32);
  const ringMat = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide, transparent: true, opacity: 0.6 });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.rotation.x = Math.PI / 2;
  group.add(ring);
  
  group.userData = { coreMat, ringMat };
  return group;
}

// Create a radial glow sprite for satellites
function createGlowSprite(color) {
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, color);
  gradient.addColorStop(0.25, color + '80');
  gradient.addColorStop(0.6, color + '20');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(canvas);
  const glowMat = new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 0.55, depthWrite: false });
  const sprite = new THREE.Sprite(glowMat);
  sprite.scale.set(0.55, 0.55, 1);
  return sprite;
}

// Create a satellite 3D model (body + solar panels)
function createSatelliteGroup(color) {
  const group = new THREE.Group();
  const bodyGeo = new THREE.BoxGeometry(0.04, 0.04, 0.08);
  const bodyMat = new THREE.MeshBasicMaterial({ color: 0xcccccc });
  group.add(new THREE.Mesh(bodyGeo, bodyMat));
  const panelGeo = new THREE.BoxGeometry(0.2, 0.005, 0.05);
  const panelMat = new THREE.MeshBasicMaterial({ color });
  group.add(new THREE.Mesh(panelGeo, panelMat));
  group.userData.panelMat = panelMat;
  return group;
}

// Dispose an object (handles Groups and regular meshes)
function disposeObj(o, scn) {
  if (!o) return;
  scn.remove(o);
  if (o.isGroup) {
    o.traverse(ch => {
      if (ch.geometry) ch.geometry.dispose();
      if (ch.material) { if (ch.material.map) ch.material.map.dispose(); ch.material.dispose(); }
    });
  } else {
    if (o.geometry) o.geometry.dispose();
    if (o.material) { if (o.material.map) o.material.map.dispose(); o.material.dispose(); }
  }
}

function SpaceScene({ activeOrbits, selectedOrbit, interactive, showLabels, cameraTargetRef, constellationOrbits, transferAnimation, showForceVectors, followSatellite, followTargetRef, onSatelliteClick, userLocation, realTimeSatellite, overheadSatellites }) {
  const { scene, camera } = useThree();
  const earthRef = useRef(null);
  const satellitePositionsRef = useRef({});

  // Persistent orbit object map
  const orbitMapRef = useRef({});
  const locationMarkerRef = useRef(null);
  const rtSatRef = useRef(null); // real-time satellite objects
  const rtTargetPos = useRef(null); // target position for lerping
  const overheadMarkersRef = useRef([]); // overhead satellite markers

  // ─── User location marker — simple pin ──────────────────────────────────────
  useEffect(() => {
    if (userLocation && userLocation.pos3d && userLocation.pos3d.length === 3) {
      if (!locationMarkerRef.current) {
        const group = new THREE.Group();

        // Pin head (small cyan sphere)
        const pin = new THREE.Mesh(
          new THREE.SphereGeometry(0.025, 12, 12),
          new THREE.MeshBasicMaterial({ color: 0x00F0FF })
        );
        pin.position.y = 0.06;
        group.add(pin);

        // Pin stem (thin line down)
        const stem = new THREE.Mesh(
          new THREE.CylinderGeometry(0.003, 0.003, 0.06, 6),
          new THREE.MeshBasicMaterial({ color: 0x00F0FF })
        );
        stem.position.y = 0.03;
        group.add(stem);

        // Glow ring at base
        const glow = new THREE.Mesh(
          new THREE.RingGeometry(0.015, 0.04, 20),
          new THREE.MeshBasicMaterial({ color: 0x00F0FF, transparent: true, opacity: 0.35, side: THREE.DoubleSide })
        );
        group.add(glow);

        // Label
        const label = createLabelSprite('YOU', '#00F0FF');
        label.position.y = 0.11;
        label.scale.set(0.12, 0.06, 1);
        group.add(label);

        locationMarkerRef.current = { group };
      }
      const marker = locationMarkerRef.current.group;
      marker.position.set(
        userLocation.pos3d[0], userLocation.pos3d[1], userLocation.pos3d[2]
      );
      marker.lookAt(0, 0, 0);
      // Flip so pin stands outward from globe
      marker.rotateX(Math.PI);
      marker.visible = true;

      if (!marker.parent || marker.parent !== scene) {
        scene.add(marker);
      }
    } else if (locationMarkerRef.current) {
      locationMarkerRef.current.group.visible = false;
    }
  }, [userLocation, scene]);

  // ─── Real-time satellite (SGP4 accurate position) ─────────────────────────
  useEffect(() => {
    if (realTimeSatellite && realTimeSatellite.pos3d) {
      const pos = realTimeSatellite.pos3d;
      // Create objects if needed
      if (!rtSatRef.current) {
        const group = new THREE.Group();

        // Satellite body (main bus)
        const bodyGeo = new THREE.BoxGeometry(0.06, 0.06, 0.1);
        const bodyMat = new THREE.MeshBasicMaterial({ color: 0xdddddd });
        group.add(new THREE.Mesh(bodyGeo, bodyMat));

        // Solar panel left
        const panelGeo = new THREE.BoxGeometry(0.22, 0.005, 0.06);
        const panelMat = new THREE.MeshBasicMaterial({ color: 0x2244AA });
        const panelL = new THREE.Mesh(panelGeo, panelMat);
        panelL.position.set(-0.14, 0, 0);
        group.add(panelL);

        // Solar panel right
        const panelR = new THREE.Mesh(panelGeo.clone(), panelMat.clone());
        panelR.position.set(0.14, 0, 0);
        group.add(panelR);

        // Panel struts
        const strutGeo = new THREE.CylinderGeometry(0.003, 0.003, 0.06, 4);
        const strutMat = new THREE.MeshBasicMaterial({ color: 0x888888 });
        const strutL = new THREE.Mesh(strutGeo, strutMat);
        strutL.position.set(-0.05, 0, 0);
        strutL.rotation.z = Math.PI / 2;
        group.add(strutL);
        const strutR = new THREE.Mesh(strutGeo.clone(), strutMat.clone());
        strutR.position.set(0.05, 0, 0);
        strutR.rotation.z = Math.PI / 2;
        group.add(strutR);

        // Antenna dish
        const antennaGeo = new THREE.ConeGeometry(0.02, 0.03, 8, 1, true);
        const antennaMat = new THREE.MeshBasicMaterial({ color: 0xeeeeee, side: THREE.DoubleSide });
        const antenna = new THREE.Mesh(antennaGeo, antennaMat);
        antenna.position.set(0, 0.05, 0);
        group.add(antenna);

        // Satellite glow
        const glowMat = new THREE.SpriteMaterial({
          map: new THREE.CanvasTexture((() => {
            const c = document.createElement('canvas');
            c.width = 64; c.height = 64;
            const ctx = c.getContext('2d');
            const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
            g.addColorStop(0, 'rgba(0,240,255,0.7)');
            g.addColorStop(0.5, 'rgba(0,240,255,0.15)');
            g.addColorStop(1, 'rgba(0,240,255,0)');
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, 64, 64);
            return c;
          })()),
          transparent: true,
        });
        const glow = new THREE.Sprite(glowMat);
        glow.scale.set(0.5, 0.5, 0.5);
        group.add(glow);

        scene.add(group);

        // Ground track line (orbit path on surface)
        const trackGeo = new THREE.BufferGeometry();
        const trackMat = new THREE.LineBasicMaterial({ color: '#00F0FF', transparent: true, opacity: 0.3 });
        const trackLine = new THREE.Line(trackGeo, trackMat);
        scene.add(trackLine);

        // Orbit path at altitude
        const orbitGeo = new THREE.BufferGeometry();
        const orbitMat = new THREE.LineBasicMaterial({ color: '#00F0FF', transparent: true, opacity: 0.5 });
        const orbitLine = new THREE.Line(orbitGeo, orbitMat);
        scene.add(orbitLine);

        // Label
        const label = createLabelSprite(realTimeSatellite.name || 'SAT', '#00F0FF');
        scene.add(label);

        rtSatRef.current = { group, trackLine, trackGeo, orbitLine, orbitGeo, label };
      }

      // Update position (set target for lerping in animation loop)
      rtTargetPos.current = new THREE.Vector3(pos[0], pos[1], pos[2]);
      rtSatRef.current.group.position.set(pos[0], pos[1], pos[2]);
      rtSatRef.current.label.position.set(pos[0], pos[1] + 0.25, pos[2]);
      rtSatRef.current.label.visible = showLabels;
      rtSatRef.current.group.visible = true;

      // Store position for camera follow
      satellitePositionsRef.current['tle_realtime'] = new THREE.Vector3(pos[0], pos[1], pos[2]);

      // Update ground track + orbit path
      if (realTimeSatellite.groundTrack && realTimeSatellite.groundTrack.length > 1) {
        const orbitPts = [];
        const surfacePts = [];
        for (const pt of realTimeSatellite.groundTrack) {
          if (pt.pos3d && pt.pos3d.length === 3) {
            orbitPts.push(new THREE.Vector3(pt.pos3d[0], pt.pos3d[1], pt.pos3d[2]));
            // Surface projection (same lat/lon but at Earth radius 2.0)
            const lat = pt.lat * Math.PI / 180;
            const lon = pt.lon * Math.PI / 180;
            const r = 2.02; // slightly above surface to avoid z-fighting
            surfacePts.push(new THREE.Vector3(
              r * Math.cos(lat) * Math.cos(lon),
              r * Math.sin(lat),
              -r * Math.cos(lat) * Math.sin(lon)
            ));
          }
        }
        if (orbitPts.length > 1) {
          rtSatRef.current.orbitGeo.dispose();
          rtSatRef.current.orbitGeo = new THREE.BufferGeometry().setFromPoints(orbitPts);
          rtSatRef.current.orbitLine.geometry = rtSatRef.current.orbitGeo;
        }
        if (surfacePts.length > 1) {
          rtSatRef.current.trackGeo.dispose();
          rtSatRef.current.trackGeo = new THREE.BufferGeometry().setFromPoints(surfacePts);
          rtSatRef.current.trackLine.geometry = rtSatRef.current.trackGeo;
        }
      }
    } else if (rtSatRef.current) {
      // Hide when no real-time satellite
      rtSatRef.current.group.visible = false;
      rtSatRef.current.trackLine.visible = false;
      rtSatRef.current.orbitLine.visible = false;
      rtSatRef.current.label.visible = false;
      rtTargetPos.current = null;
    }
  }, [realTimeSatellite, scene, showLabels]);

  // ─── Overhead satellite markers ────────────────────────────────────────────
  useEffect(() => {
    // Clean up old markers
    for (const m of overheadMarkersRef.current) {
      scene.remove(m.group);
      scene.remove(m.label);
    }
    overheadMarkersRef.current = [];

    if (overheadSatellites && overheadSatellites.length > 0) {
      for (const sat of overheadSatellites) {
        if (!sat.pos3d || sat.pos3d.length !== 3) continue;
        const group = new THREE.Group();

        // Small glowing sphere
        const sphereGeo = new THREE.SphereGeometry(0.04, 8, 8);
        const sphereMat = new THREE.MeshBasicMaterial({ color: 0xFF9500 });
        group.add(new THREE.Mesh(sphereGeo, sphereMat));

        // Glow sprite
        const glowMat = new THREE.SpriteMaterial({
          map: new THREE.CanvasTexture((() => {
            const c = document.createElement('canvas');
            c.width = 64; c.height = 64;
            const ctx = c.getContext('2d');
            const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
            g.addColorStop(0, 'rgba(255,149,0,0.6)');
            g.addColorStop(0.5, 'rgba(255,149,0,0.12)');
            g.addColorStop(1, 'rgba(255,149,0,0)');
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, 64, 64);
            return c;
          })()),
          transparent: true,
        });
        const glow = new THREE.Sprite(glowMat);
        glow.scale.set(0.35, 0.35, 0.35);
        group.add(glow);

        group.position.set(sat.pos3d[0], sat.pos3d[1], sat.pos3d[2]);
        scene.add(group);

        // Label
        const label = createLabelSprite(sat.satellite_name || 'SAT', '#FF9500');
        label.position.set(sat.pos3d[0], sat.pos3d[1] + 0.18, sat.pos3d[2]);
        label.visible = showLabels;
        scene.add(label);

        overheadMarkersRef.current.push({ group, label });
      }
    }

    return () => {
      for (const m of overheadMarkersRef.current) {
        scene.remove(m.group);
        scene.remove(m.label);
      }
      overheadMarkersRef.current = [];
    };
  }, [overheadSatellites, scene, showLabels]);

  // ─── Static scene: lights + stars + earth ───────────────────────────────
  useEffect(() => {
    const objs = [];

    const ambient = new THREE.AmbientLight(0xffffff, 0.55);
    const sun = new THREE.DirectionalLight(0xfff8ee, 2.1);
    sun.position.set(8, 4, 6);
    const fill = new THREE.DirectionalLight(0xaaccff, 0.35);
    fill.position.set(-6, -3, -4);
    scene.add(ambient, sun, fill);
    objs.push(ambient, sun, fill);

    // Stars
    const count = 5000;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 130 + Math.random() * 70;
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const starMat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.22, sizeAttenuation: true, transparent: true, opacity: 0.9 });
    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);
    objs.push(stars);

    // Earth
    const earthGeo = new THREE.SphereGeometry(EARTH_R, 64, 64);
    const earthMat = new THREE.MeshPhongMaterial({ color: 0x1a3d6e, emissive: 0x060f1e, shininess: 25 });
    const earth = new THREE.Mesh(earthGeo, earthMat);
    earthRef.current = earth;
    scene.add(earth);
    objs.push(earth);

    new THREE.TextureLoader().load(EARTH_TEXTURE_URL, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      earthMat.map = tex;
      earthMat.color.setHex(0xffffff);
      earthMat.emissive.setHex(0x000000);
      earthMat.needsUpdate = true;
    });

    // Atmosphere
    const atmoGeo = new THREE.SphereGeometry(EARTH_R * 1.025, 32, 32);
    const atmoMat = new THREE.MeshPhongMaterial({ color: 0x4499ff, transparent: true, opacity: 0.1, side: THREE.BackSide });
    const atmo = new THREE.Mesh(atmoGeo, atmoMat);
    scene.add(atmo); objs.push(atmo);

    return () => {
      objs.forEach(o => {
        scene.remove(o);
        if (o.geometry) o.geometry.dispose();
        if (o.material) { if (o.material.map) o.material.map.dispose(); o.material.dispose(); }
      });
    };
  }, [scene]);

  // ─── Sync orbit objects with activeOrbits ────────────────────────────────
  useEffect(() => {
    const targetIds = new Set(activeOrbits.filter(Boolean).map(o => o.id));

    // Remove obsolete
    Object.keys(orbitMapRef.current).forEach(id => {
      if (!targetIds.has(id)) {
        const e = orbitMapRef.current[id];
        [e.line, e.sat, e.trailLine, e.label, e.glow].forEach(o => disposeObj(o, scene));
        if (e.trailMat) e.trailMat.dispose();
        delete orbitMapRef.current[id];
      }
    });

    // Add or update orbits
    activeOrbits.filter(Boolean).forEach(orbit => {
      const existing = orbitMapRef.current[orbit.id];
      if (existing) {
        // Detect parameter changes — update in-place for smooth transitions
        const o = existing.orbit;
        const geomChanged = o.semiMajor !== orbit.semiMajor || o.eccentricity !== orbit.eccentricity ||
                            o.inclination !== orbit.inclination || o.raan !== orbit.raan;
        const colorChanged = o.color !== orbit.color;
        const nameChanged = o.shortName !== orbit.shortName;
        if (!geomChanged && !colorChanged && !nameChanged && o.speed === orbit.speed) {
          // Still update orbit reference for overrideT changes
          existing.orbit = orbit;
          return;
        }

        existing.orbit = orbit;

        if (geomChanged) {
          const newPoints = computeOrbitPoints(orbit.semiMajor, orbit.eccentricity, orbit.inclination, orbit.raan, 300);
          existing.points = newPoints;
          existing.times = buildTimeArray(newPoints);
          existing.line.geometry.dispose();
          existing.line.geometry = new THREE.BufferGeometry().setFromPoints(newPoints);
          existing.trailHead = 0;
          existing.trailCount = 0;
          existing.trailGeo.setDrawRange(0, 0);
        }

        if (colorChanged || nameChanged) {
          if (colorChanged) {
            existing.lineMat.color.setStyle(orbit.color);
            existing.satMat.color.setStyle(orbit.color);
            existing.trailColor.setStyle(orbit.color);
            disposeObj(existing.glow, scene);
            existing.glow = createGlowSprite(orbit.color);
            scene.add(existing.glow);
          }
          disposeObj(existing.label, scene);
          existing.label = createLabelSprite(orbit.shortName, orbit.color);
          existing.label.visible = showLabels;
          scene.add(existing.label);
        }
        return;
      }

      const points = computeOrbitPoints(orbit.semiMajor, orbit.eccentricity, orbit.inclination, orbit.raan, 300);
      const times = buildTimeArray(points);

      // Orbit line
      const lineGeo = new THREE.BufferGeometry().setFromPoints(points);
      const lineMat = new THREE.LineBasicMaterial({ color: orbit.color, transparent: true, opacity: 0.65 });
      const line = new THREE.Line(lineGeo, lineMat);
      scene.add(line);

      // Trail with vertex colors for gradient fade
      const trailBuf = new Array(TRAIL_LEN).fill(null).map(() => new THREE.Vector3());
      const trailPos = new Float32Array(TRAIL_LEN * 3);
      const trailColorArr = new Float32Array(TRAIL_LEN * 3);
      const trailGeo = new THREE.BufferGeometry();
      trailGeo.setAttribute('position', new THREE.BufferAttribute(trailPos, 3));
      trailGeo.setAttribute('color', new THREE.BufferAttribute(trailColorArr, 3));
      trailGeo.setDrawRange(0, 0);
      const trailMat = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.75 });
      const trailLine = new THREE.Line(trailGeo, trailMat);
      trailLine.frustumCulled = false;
      scene.add(trailLine);

      // Satellite (skip if noSatellite flag is set, e.g., for Hohmann reference orbits)
      let sat, satMat, glow;
      if (orbit.noSatellite) {
        // Create invisible placeholder so animation loop doesn't break
        sat = new THREE.Group();
        sat.visible = false;
        satMat = new THREE.MeshBasicMaterial({ visible: false });
        glow = new THREE.Sprite(new THREE.SpriteMaterial({ visible: false, transparent: true, opacity: 0 }));
      } else {
        sat = createSatelliteGroup(orbit.color);
        satMat = sat.userData.panelMat;
        glow = createGlowSprite(orbit.color);
      }
      scene.add(sat);
      scene.add(glow);

      // Label sprite
      const label = createLabelSprite(orbit.shortName, orbit.color);
      label.visible = false;
      scene.add(label);

      orbitMapRef.current[orbit.id] = {
        orbit, line, lineMat, sat, satMat, glow,
        trailLine, trailGeo, trailMat, trailBuf, trailHead: 0, trailCount: 0,
        label, points, times, trailColor: new THREE.Color(orbit.color),
        t: Math.random(),
      };
    });
  }, [scene, activeOrbits]);

  // ─── Update opacity when selectedOrbit changes ───────────────────────────
  useEffect(() => {
    Object.values(orbitMapRef.current).forEach(e => {
      const isHighlighted = selectedOrbit?.id === e.orbit.id;
      const opacity = isHighlighted ? 0.95 : (selectedOrbit ? 0.18 : 0.65);
      e.lineMat.opacity = opacity;
      e.trailMat.opacity = isHighlighted ? 0.85 : 0.5;
      e.satMat.color.setStyle(e.orbit.color);
      e.sat.scale.setScalar(isHighlighted ? 1.4 : 1);
      if (e.glow) {
        e.glow.scale.setScalar(isHighlighted ? 0.8 : 0.55);
        e.glow.material.opacity = isHighlighted ? 0.7 : 0.4;
      }
    });
  }, [selectedOrbit]);

  // ─── Update labels visibility ─────────────────────────────────────────────
  useEffect(() => {
    Object.values(orbitMapRef.current).forEach(e => {
      e.label.visible = showLabels;
    });
  }, [showLabels]);

  // ─── GPS Constellation objects (separate, persistent) ─────────────────────
  const constellationRef = useRef([]);
  useEffect(() => {
    // Clear previous
    constellationRef.current.forEach(o => { 
      scene.remove(o); 
      if (o.geometry) o.geometry.dispose(); 
      if (o.material) o.material.dispose(); 
    });
    constellationRef.current = [];
    if (!constellationOrbits?.length) return;

    constellationOrbits.forEach(co => {
      const pts = computeOrbitPoints(co.semiMajor, co.eccentricity, co.inclination, co.raan, 100);
      if (!pts || pts.length === 0) return;
      
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineBasicMaterial({ color: co.color, transparent: true, opacity: 0.22 });
      const line = new THREE.Line(geo, mat);
      scene.add(line);
      constellationRef.current.push(line);

      const satGeo = new THREE.SphereGeometry(0.04, 6, 6);
      const satMat = new THREE.MeshBasicMaterial({ color: co.color });
      const sat = new THREE.Mesh(satGeo, satMat);
      // Ensure phase is clamped to valid range [0, 1) and index is within bounds
      const phase = Math.max(0, Math.min(co.phase || 0, 0.999));
      const satIdx = Math.min(Math.floor(phase * pts.length), pts.length - 1);
      sat.position.copy(pts[satIdx]);
      scene.add(sat);
      constellationRef.current.push(sat);
    });

    return () => {
      constellationRef.current.forEach(o => { 
        scene.remove(o); 
        if (o.geometry) o.geometry.dispose(); 
        if (o.material) o.material.dispose(); 
      });
      constellationRef.current = [];
    };
  }, [scene, constellationOrbits]);

  // ─── Hohmann Transfer Animation ──────────────────────────────────────────
  const transferAnimRef = useRef(null);
  useEffect(() => {
    if (transferAnimRef.current) {
      const tr = transferAnimRef.current;
      [tr.sat, tr.glow, tr.burnSprite].forEach(o => disposeObj(o, scene));
      transferAnimRef.current = null;
    }
    if (!transferAnimation) return;
    const { startOrbit, transferOrbit, endOrbit } = transferAnimation;
    const paths = [startOrbit, transferOrbit, endOrbit].map(o => {
      const points = computeOrbitPoints(o.semiMajor, o.eccentricity, o.inclination, o.raan, 300);
      return { ...o, points, times: buildTimeArray(points) };
    });
    const sat = createSatelliteGroup('#FFFFFF');
    sat.scale.setScalar(1.5);
    scene.add(sat);
    const glow = createGlowSprite('#FFFFFF');
    glow.scale.set(0.7, 0.7, 1);
    scene.add(glow);
    // Burn exhaust
    const bc = document.createElement('canvas');
    bc.width = 64; bc.height = 64;
    const bx = bc.getContext('2d');
    const bg = bx.createRadialGradient(32, 32, 0, 32, 32, 32);
    bg.addColorStop(0, '#FFCC00');
    bg.addColorStop(0.3, '#FF660080');
    bg.addColorStop(1, 'rgba(0,0,0,0)');
    bx.fillStyle = bg;
    bx.fillRect(0, 0, 64, 64);
    const burnMat = new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(bc), transparent: true, opacity: 0, depthWrite: false });
    const burnSprite = new THREE.Sprite(burnMat);
    burnSprite.scale.set(0.8, 0.8, 1);
    scene.add(burnSprite);
    transferAnimRef.current = { sat, glow, burnSprite, paths, phase: 0, t: 0.25, phaseTime: 0, _reported: -1 };
    return () => {
      if (transferAnimRef.current) {
        [transferAnimRef.current.sat, transferAnimRef.current.glow, transferAnimRef.current.burnSprite].forEach(o => disposeObj(o, scene));
        transferAnimRef.current = null;
      }
    };
  }, [scene, transferAnimation]);

  // ─── Force Vector Arrows ─────────────────────────────────────────────────
  const forceVecRef = useRef({ gravity: null, velocity: null });
  useEffect(() => {
    const fv = forceVecRef.current;
    if (fv.gravity) { scene.remove(fv.gravity); }
    if (fv.velocity) { scene.remove(fv.velocity); }
    forceVecRef.current = { gravity: null, velocity: null };
    if (!showForceVectors) return;
    const gravArrow = new THREE.ArrowHelper(new THREE.Vector3(0, -1, 0), new THREE.Vector3(), 1.0, 0xFF4444, 0.15, 0.08);
    const velArrow = new THREE.ArrowHelper(new THREE.Vector3(1, 0, 0), new THREE.Vector3(), 1.5, 0x44AAFF, 0.15, 0.08);
    scene.add(gravArrow);
    scene.add(velArrow);
    forceVecRef.current = { gravity: gravArrow, velocity: velArrow };
    return () => {
      scene.remove(forceVecRef.current.gravity);
      scene.remove(forceVecRef.current.velocity);
      forceVecRef.current = { gravity: null, velocity: null };
    };
  }, [scene, showForceVectors]);

  // ─── Lagrange Points Visualization ────────────────────────────────────────
  const lagrangeRef = useRef([]);
  const showLagrange = activeOrbits.some(o => o?.id === 'lagrange');
  
  useEffect(() => {
    // Clean up existing
    lagrangeRef.current.forEach(obj => disposeObj(obj, scene));
    lagrangeRef.current = [];
    
    if (!showLagrange) return;
    
    // Add Sun representation (to the left)
    const sunGeo = new THREE.SphereGeometry(1.5, 32, 32);
    const sunMat = new THREE.MeshBasicMaterial({ color: 0xFFDD44 });
    const sun = new THREE.Mesh(sunGeo, sunMat);
    sun.position.set(-18, 0, 0);
    scene.add(sun);
    lagrangeRef.current.push(sun);
    
    // Sun glow
    const glowCanvas = document.createElement('canvas');
    glowCanvas.width = 128; glowCanvas.height = 128;
    const glowCtx = glowCanvas.getContext('2d');
    const gradient = glowCtx.createRadialGradient(64, 64, 0, 64, 64, 64);
    gradient.addColorStop(0, '#FFDD4480');
    gradient.addColorStop(0.3, '#FFaa2240');
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    glowCtx.fillStyle = gradient;
    glowCtx.fillRect(0, 0, 128, 128);
    const sunGlowMat = new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(glowCanvas), transparent: true, opacity: 0.7 });
    const sunGlow = new THREE.Sprite(sunGlowMat);
    sunGlow.position.set(-18, 0, 0);
    sunGlow.scale.set(8, 8, 1);
    scene.add(sunGlow);
    lagrangeRef.current.push(sunGlow);
    
    // Sun label
    const sunLabel = createLabelSprite('SUN', '#FFD700');
    sunLabel.position.set(-18, 2.5, 0);
    scene.add(sunLabel);
    lagrangeRef.current.push(sunLabel);
    
    // Earth orbit line (simplified, circular)
    const earthOrbitRadius = 18;
    const earthOrbitPoints = [];
    for (let i = 0; i <= 64; i++) {
      const angle = (i / 64) * Math.PI * 2;
      earthOrbitPoints.push(new THREE.Vector3(
        -18 + earthOrbitRadius * Math.cos(angle),
        0,
        earthOrbitRadius * Math.sin(angle)
      ));
    }
    const earthOrbitGeo = new THREE.BufferGeometry().setFromPoints(earthOrbitPoints);
    const earthOrbitMat = new THREE.LineBasicMaterial({ color: 0x4488FF, transparent: true, opacity: 0.25 });
    const earthOrbitLine = new THREE.Line(earthOrbitGeo, earthOrbitMat);
    scene.add(earthOrbitLine);
    lagrangeRef.current.push(earthOrbitLine);
    
    // Add each Lagrange point
    Object.entries(LAGRANGE_POINTS).forEach(([key, lp]) => {
      const marker = createLagrangeMarker(lp.color);
      marker.position.set(...lp.position);
      scene.add(marker);
      lagrangeRef.current.push(marker);
      
      const label = createLabelSprite(lp.label, lp.color);
      label.position.set(lp.position[0], lp.position[1] + 0.7, lp.position[2]);
      scene.add(label);
      lagrangeRef.current.push(label);
    });
    
    // Sun-Earth line
    const lineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-18, 0, 0),
      new THREE.Vector3(0, 0, 0)
    ]);
    const lineMat = new THREE.LineDashedMaterial({ color: 0xFFDD44, dashSize: 0.5, gapSize: 0.3, transparent: true, opacity: 0.4 });
    const line = new THREE.Line(lineGeo, lineMat);
    line.computeLineDistances();
    scene.add(line);
    lagrangeRef.current.push(line);
    
    return () => {
      lagrangeRef.current.forEach(obj => disposeObj(obj, scene));
      lagrangeRef.current = [];
    };
  }, [scene, showLagrange]);

  // ─── Animation loop ────────────────────────────────────────────────────────
  const pulseRef = useRef(0);
  useFrame((_, delta) => {
    pulseRef.current += delta;

    // Rotate Earth
    if (earthRef.current) {
      // Stop Earth rotation when tracking or location marker is active
      // so lat/lon coordinates match the texture
      if (realTimeSatellite || userLocation) {
        earthRef.current.rotation.y = 0;
      } else {
        earthRef.current.rotation.y += delta * 0.025;
      }
    }

    // Pulse Lagrange markers
    if (showLagrange && lagrangeRef.current.length > 0) {
      lagrangeRef.current.forEach(obj => {
        if (obj.isGroup && obj.userData?.ringMat) {
          const pulse = 0.4 + Math.sin(pulseRef.current * 2) * 0.2;
          obj.userData.ringMat.opacity = pulse;
          const scale = 1 + Math.sin(pulseRef.current * 1.5) * 0.1;
          obj.scale.setScalar(scale);
        }
      });
    }

    // ── Hohmann transfer animation
    if (transferAnimRef.current) {
      const tr = transferAnimRef.current;
      tr.phaseTime += delta;
      const getPos = (pi, t) => {
        const p = tr.paths[pi];
        const idx = findIdx(p.times, t);
        const ni = Math.min(idx + 1, p.points.length - 1);
        const f = idx < p.times.length - 1 ? (t - p.times[idx]) / Math.max(p.times[ni] - p.times[idx], 1e-6) : 0;
        return new THREE.Vector3().lerpVectors(p.points[idx], p.points[ni], f);
      };
      let pos = null;
      switch (tr.phase) {
        case 0: { // LEO orbit
          const spd = (tr.paths[0].speed || 0.15) * 0.18;
          tr.t = (tr.t + spd * delta) % 1;
          pos = getPos(0, tr.t);
          tr.burnSprite.material.opacity = Math.max(0, tr.burnSprite.material.opacity - delta * 3);
          if (tr.phaseTime > 4) { tr.phase = 1; tr.phaseTime = 0; }
          break;
        }
        case 1: { // Prograde burn at perigee
          pos = getPos(0, tr.t);
          tr.burnSprite.material.opacity = 0.8 + Math.sin(tr.phaseTime * 12) * 0.2;
          tr.burnSprite.scale.setScalar(0.5 + Math.sin(tr.phaseTime * 8) * 0.2);
          if (tr.phaseTime > 2) { tr.phase = 2; tr.phaseTime = 0; tr.t = 0; }
          break;
        }
        case 2: { // Transfer coast (perigee→apogee, t:0→0.5)
          const spd = (tr.paths[1].speed || 0.04) * 0.18;
          tr.t = Math.min(tr.t + spd * delta, 0.5);
          pos = getPos(1, tr.t);
          tr.burnSprite.material.opacity = Math.max(0, tr.burnSprite.material.opacity - delta * 2);
          if (tr.t >= 0.499) { tr.phase = 3; tr.phaseTime = 0; }
          break;
        }
        case 3: { // Circularization burn at apogee
          pos = getPos(1, 0.5);
          tr.burnSprite.material.opacity = 0.8 + Math.sin(tr.phaseTime * 12) * 0.2;
          tr.burnSprite.scale.setScalar(0.5 + Math.sin(tr.phaseTime * 8) * 0.2);
          if (tr.phaseTime > 2) {
            const ap = getPos(1, 0.5);
            let bestT = 0, bestD = Infinity;
            tr.paths[2].points.forEach((pt, i) => {
              const d = pt.distanceTo(ap);
              if (d < bestD) { bestD = d; bestT = i / tr.paths[2].points.length; }
            });
            tr.t = bestT; tr.phase = 4; tr.phaseTime = 0;
          }
          break;
        }
        case 4: { // GEO orbit
          const spd = (tr.paths[2].speed || 0.04) * 0.18;
          tr.t = (tr.t + spd * delta) % 1;
          pos = getPos(2, tr.t);
          tr.burnSprite.material.opacity = Math.max(0, tr.burnSprite.material.opacity - delta * 2);
          if (tr.phaseTime > 6) { tr.phase = 0; tr.phaseTime = 0; tr.t = 0.25; }
          break;
        }
        default: break;
      }
      if (pos) {
        tr.sat.position.copy(pos);
        tr.glow.position.copy(pos);
        tr.burnSprite.position.copy(pos);
      }
      if (tr.phase !== tr._reported) {
        tr._reported = tr.phase;
        if (transferAnimation?.onPhaseChange) transferAnimation.onPhaseChange(tr.phase);
      }
    }

    // Animate each orbit's satellite with smooth lerp + Keplerian speed
    Object.values(orbitMapRef.current).forEach(e => {
      // Support override angle from external controllers (e.g., CatchUpChallenge)
      if (e.orbit.overrideT !== undefined && e.orbit.overrideT !== null) {
        e.t = e.orbit.overrideT % 1;
      } else {
        const spd = (e.orbit.speed || 0.1) * 0.18;
        e.t = (e.t + spd * delta) % 1;
      }

      // Keplerian position: find index via time array
      const idx = findIdx(e.times, e.t);
      const nextIdx = Math.min(idx + 1, e.points.length - 1);
      const frac = idx < e.times.length - 1
        ? (e.t - e.times[idx]) / Math.max(e.times[nextIdx] - e.times[idx], 1e-6)
        : 0;

      // Smooth lerp between adjacent points
      const newPos = new THREE.Vector3().lerpVectors(e.points[idx], e.points[nextIdx], frac);

      // Skip visual updates for noSatellite orbits (Hohmann reference lines)
      if (e.orbit.noSatellite) {
        // Still store position and update label position
        satellitePositionsRef.current[e.orbit.id] = newPos.clone();
        if (e.label.visible) {
          e.label.position.copy(newPos);
          e.label.position.y += 0.28;
        }
        return;
      }

      e.sat.position.copy(newPos);

      // Orient satellite along orbit tangent
      const tangent = new THREE.Vector3().subVectors(e.points[nextIdx], e.points[idx]);
      if (tangent.lengthSq() > 0.0001) {
        e.sat.lookAt(newPos.clone().add(tangent.normalize()));
      }

      // Pulse scale for highlighted satellite
      if (selectedOrbit?.id === e.orbit.id) {
        const pScale = 1.3 + Math.sin(pulseRef.current * 3) * 0.2;
        e.sat.scale.setScalar(pScale);
        if (e.glow) {
          e.glow.scale.setScalar(0.8 + Math.sin(pulseRef.current * 2) * 0.15);
          e.glow.material.opacity = 0.6 + Math.sin(pulseRef.current * 2.5) * 0.15;
        }
      }

      // Update trail circular buffer
      e.trailBuf[e.trailHead % TRAIL_LEN].copy(newPos);
      e.trailHead++;
      e.trailCount = Math.min(e.trailCount + 1, TRAIL_LEN);

      if (e.trailCount > 1) {
        const posAttr = e.trailGeo.attributes.position;
        const colorAttr = e.trailGeo.attributes.color;
        const start = e.trailHead % TRAIL_LEN;
        for (let i = 0; i < e.trailCount; i++) {
          const bufIdx = (start + i) % TRAIL_LEN;
          const p = e.trailBuf[bufIdx];
          posAttr.setXYZ(i, p.x, p.y, p.z);
          // Gradient: oldest=dark, newest=bright
          const fade = i / Math.max(1, e.trailCount - 1);
          const tc = e.trailColor;
          colorAttr.setXYZ(i, tc.r * fade, tc.g * fade, tc.b * fade);
        }
        posAttr.needsUpdate = true;
        colorAttr.needsUpdate = true;
        e.trailGeo.setDrawRange(0, e.trailCount);
      }

      // Update glow position
      if (e.glow) e.glow.position.copy(newPos);

      // Update force vectors for selected orbit
      if (showForceVectors && forceVecRef.current.gravity && selectedOrbit?.id === e.orbit.id) {
        const gravDir = newPos.clone().negate().normalize();
        const velDir = new THREE.Vector3().subVectors(e.points[nextIdx], e.points[idx]).normalize();
        forceVecRef.current.gravity.position.copy(newPos);
        forceVecRef.current.gravity.setDirection(gravDir);
        forceVecRef.current.gravity.setLength(0.8, 0.12, 0.06);
        forceVecRef.current.velocity.position.copy(newPos);
        forceVecRef.current.velocity.setDirection(velDir);
        const speedScale = Math.min(2.5, (1 / Math.max(0.5, newPos.length())) * 4);
        forceVecRef.current.velocity.setLength(speedScale, 0.12, 0.06);
      }

      // Update label position (above satellite)
      if (e.label.visible) {
        e.label.position.copy(newPos);
        e.label.position.y += 0.28;
      }

      // Store satellite position for camera follow
      satellitePositionsRef.current[e.orbit.id] = newPos.clone();
    });

    // Camera follow mode - smoothly track the followed satellite
    if (followSatellite && satellitePositionsRef.current[followSatellite]) {
      const satPos = satellitePositionsRef.current[followSatellite];
      const followDist = 3.5;  // Distance behind satellite
      const followHeight = 1.5;  // Height above satellite
      
      // Calculate position behind satellite (opposite to orbit tangent direction)
      const entry = Object.values(orbitMapRef.current).find(e => e.orbit.id === followSatellite);
      if (entry) {
        const idx = findIdx(entry.times, entry.t);
        const nextIdx = Math.min(idx + 1, entry.points.length - 1);
        const tangent = new THREE.Vector3().subVectors(entry.points[nextIdx], entry.points[idx]).normalize();
        
        // Position camera behind and above satellite
        const targetCamPos = satPos.clone()
          .sub(tangent.multiplyScalar(followDist))
          .add(new THREE.Vector3(0, followHeight, 0));
        
        // Smooth interpolation
        camera.position.lerp(targetCamPos, 0.05);
        
        // Look at satellite
        const lookTarget = satPos.clone();
        camera.lookAt(lookTarget);
      }
    }
  });

  return <CameraControls enabled={interactive && !followSatellite} targetRef={cameraTargetRef} />;
}

export function EarthScene({
  activeOrbits = [],
  selectedOrbit = null,
  interactive = true,
  height = '100%',
  cameraPosition = [0, 4, 12],
  showLabels = false,
  constellationOrbits = null,
  transferAnimation = null,
  showForceVectors = false,
  followSatellite = null,
  onSatelliteClick = null,
  userLocation = null,
  realTimeSatellite = null,
  overheadSatellites = null,
}) {
  const cameraTargetRef = useRef(new THREE.Vector3(...cameraPosition));
  const followTargetRef = useRef(null);

  // Update camera target when selected orbit changes
  useEffect(() => {
    if (selectedOrbit?.semiMajor) {
      const dist = selectedOrbit.semiMajor * 2.8;
      cameraTargetRef.current.set(0, dist * 0.3, dist);
    } else {
      cameraTargetRef.current.set(...cameraPosition);
    }
  }, [selectedOrbit?.id]);

  return (
    <div style={{ width: '100%', height, position: 'relative' }}>
      <Canvas
        camera={{ position: cameraPosition, fov: 45, near: 0.1, far: 600 }}
        gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
        dpr={[1, 1.5]}
        style={{ background: '#030305' }}
      >
        <SpaceScene
          activeOrbits={activeOrbits.filter(Boolean)}
          selectedOrbit={selectedOrbit}
          interactive={interactive}
          showLabels={showLabels}
          cameraTargetRef={cameraTargetRef}
          constellationOrbits={constellationOrbits}
          transferAnimation={transferAnimation}
          showForceVectors={showForceVectors}
          followSatellite={followSatellite}
          followTargetRef={followTargetRef}
          onSatelliteClick={onSatelliteClick}
          userLocation={userLocation}
          realTimeSatellite={realTimeSatellite}
          overheadSatellites={overheadSatellites}
        />
      </Canvas>
    </div>
  );
}
